import { AlertCircle, CheckCircle2, ChevronDown, Mic, MicOff, Settings, Video, VideoOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";

type CallSettingsProps = {
    onPermissionStateChange?: (state: MediaPermissionState) => void;
};

export type MediaPermissionState = 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported';

type BrowserPermissionState = 'granted' | 'denied' | 'prompt' | 'unavailable';

const MESSAGES = {
    section: {
        readyTitle: 'Câmera e microfone prontos',
        stepTitle: 'Primeiro passo: liberar câmera e microfone',
        readyDescription: 'Agora confira sua imagem e seu áudio antes de entrar na sala.',
        stepDescription: 'Quando o navegador perguntar, toque em Permitir. Sem isso você pode ficar sem áudio ou vídeo na sessão.',
    },
    button: {
        requesting: 'Solicitando permissão...',
        retry: 'Tentar liberar novamente',
        unsupported: 'Seu navegador não suporta câmera/microfone',
        request: 'Liberar câmera e microfone',
    },
    deniedHelp: {
        title: 'Permissão bloqueada no navegador',
        browser: 'Abra o cadeado ao lado da URL e altere Câmera e Microfone para Permitir. Em seguida, recarregue a página.',
        safari: 'No Safari (macOS): Safari > Ajustes > Sites > Câmera/Microfone.',
    },
    preview: {
        waitingVideo: 'Seu vídeo vai aparecer aqui assim que a permissão for liberada.',
    },
    labels: {
        camera: 'Câmera',
        microphone: 'Microfone',
        fallbackCamera: 'Câmera',
        fallbackMicrophone: 'Microfone',
    },
} as const;

const CallSettings: React.FC<CallSettingsProps> = ({ onPermissionStateChange }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { updateMediaSettings } = useAuth();

    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
    const [showSettings, setShowSettings] = useState(false);
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [permissionState, setPermissionState] = useState<MediaPermissionState>('idle');

    useEffect(() => {
        onPermissionStateChange?.(permissionState);
    }, [onPermissionStateChange, permissionState]);

    const setupDevicesFromStream = useCallback(async (mediaStream: MediaStream) => {
        try {
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            setDevices(deviceList);

            const videoInput = deviceList.find(d => d.kind === 'videoinput');
            const audioInput = deviceList.find(d => d.kind === 'audioinput');

            if (videoInput) setSelectedVideoDevice(videoInput.deviceId);
            if (audioInput) setSelectedAudioDevice(audioInput.deviceId);
        } catch (error) {
            console.error('Erro ao obter permissão ou dispositivos:', error);
        }
        setStream(mediaStream);
        if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
        }
        setPermissionState('granted');
    }, []);

    const getBrowserMediaPermissionState = useCallback(async (): Promise<BrowserPermissionState> => {
        if (!navigator.permissions?.query) {
            return 'unavailable';
        }

        try {
            const [cameraPermission, microphonePermission] = await Promise.all([
                navigator.permissions.query({ name: 'camera' as PermissionName }),
                navigator.permissions.query({ name: 'microphone' as PermissionName }),
            ]);

            if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
                return 'denied';
            }

            if (cameraPermission.state === 'granted' && microphonePermission.state === 'granted') {
                return 'granted';
            }

            return 'prompt';
        } catch (error) {
            console.error('Erro ao consultar estado de permissão do navegador:', error);
            return 'unavailable';
        }
    }, []);

    const requestMediaAccess = useCallback(async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setPermissionState('unsupported');
            return;
        }

        setPermissionState('requesting');

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            await setupDevicesFromStream(mediaStream);
        } catch (error) {
            console.error('Erro ao obter permissão ou dispositivos:', error);
            setPermissionState('denied');
        }
    }, [setupDevicesFromStream]);

    useEffect(() => {
        let isMounted = true;

        const initializeMediaPermissionState = async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                setPermissionState('unsupported');
                return;
            }

            const browserPermissionState = await getBrowserMediaPermissionState();
            if (!isMounted) {
                return;
            }

            if (browserPermissionState === 'denied') {
                setPermissionState('denied');
                return;
            }

            if (browserPermissionState === 'granted') {
                await requestMediaAccess();
            }
        };

        initializeMediaPermissionState();

        return () => {
            isMounted = false;
        };
    }, [getBrowserMediaPermissionState, requestMediaAccess]);

    const updateStream = useCallback(async () => {
        if (permissionState !== 'granted') {
            return;
        }

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        try {
            const constraints: MediaStreamConstraints = {
                video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
                audio: selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true,
            };
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            console.error('Erro ao acessar a câmera:', error);
            setPermissionState('denied');
        }
    }, [permissionState, selectedVideoDevice, selectedAudioDevice, stream]);

    useEffect(() => {
        if (permissionState === 'granted' && (selectedVideoDevice || selectedAudioDevice)) {
            updateStream();
        }

        return () => {
            stream?.getTracks().forEach(track => track.stop());
        };
    }, [permissionState, selectedVideoDevice, selectedAudioDevice]);

    const toggleVideo = () => {
        if (!stream) {
            return;
        }

        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];
        if (videoTrack) {
            videoTrack.enabled = !videoTrack.enabled;
            setIsVideoEnabled(videoTrack.enabled);
            updateMediaSettings({ 
                video: videoTrack.enabled, 
                audio: audioTrack?.enabled ?? true 
            });
        }
    };

    const toggleAudio = () => {
        if (!stream) {
            return;
        }

        const audioTrack = stream.getAudioTracks()[0];
        const videoTrack = stream.getVideoTracks()[0];
        if (audioTrack) {
            audioTrack.enabled = !audioTrack.enabled;
            setIsAudioEnabled(audioTrack.enabled);
            updateMediaSettings({ 
                video: videoTrack?.enabled ?? true, 
                audio: audioTrack.enabled 
            });
        }
    };

    return (
        <div id="meeting-media-setup" className="flex flex-col lg:flex-row w-full gap-4 px-4 pb-4 md:px-6 md:pb-6 items-start justify-center">
            <div className="w-full lg:w-1/2">
                <div className="mb-4 rounded-2xl border border-teal-100 bg-teal-50/70 p-4">
                    <div className="flex items-start gap-3">
                        {permissionState === 'granted' ? (
                            <CheckCircle2 className="mt-0.5 h-5 w-5 text-teal-600" />
                        ) : (
                            <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
                        )}
                        <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900">
                                {permissionState === 'granted'
                                    ? MESSAGES.section.readyTitle
                                    : MESSAGES.section.stepTitle}
                            </h4>
                            <p className="mt-1 text-sm text-gray-600">
                                {permissionState === 'granted'
                                    ? MESSAGES.section.readyDescription
                                    : MESSAGES.section.stepDescription}
                            </p>
                        </div>
                    </div>

                    {permissionState !== 'granted' && (
                        <button
                            type="button"
                            onClick={requestMediaAccess}
                            disabled={permissionState === 'requesting'}
                            className="mt-4 w-full rounded-xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                        >
                            {permissionState === 'requesting'
                                ? MESSAGES.button.requesting
                                : permissionState === 'denied'
                                    ? MESSAGES.button.retry
                                    : permissionState === 'unsupported'
                                        ? MESSAGES.button.unsupported
                                        : MESSAGES.button.request}
                        </button>
                    )}

                    {permissionState === 'denied' && (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                            <p className="font-semibold">{MESSAGES.deniedHelp.title}</p>
                            <p className="mt-1">
                                {MESSAGES.deniedHelp.browser}
                            </p>
                            <p className="mt-1">
                                {MESSAGES.deniedHelp.safari}
                            </p>
                        </div>
                    )}
                </div>

                <div className="relative bg-black rounded-2xl overflow-hidden mb-6 mx-auto max-w-sm lg:max-w-none" style={{ aspectRatio: '16/10' }}>
                    <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover transition-opacity duration-300 ${!isVideoEnabled ? 'opacity-0' : 'opacity-100'}`} />
                    {permissionState !== 'granted' ? (
                        <div className="absolute inset-0 bg-gray-900/90 flex items-center justify-center px-6 text-center">
                            <div>
                                <VideoOff className="mx-auto mb-3 h-12 w-12 text-gray-400" />
                                <p className="text-sm font-medium text-white">{MESSAGES.preview.waitingVideo}</p>
                            </div>
                        </div>
                    ) : !isVideoEnabled ? (
                        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center"><VideoOff className="w-12 h-12 text-gray-400" /></div>
                    ) : null}
                </div>
                <div className="flex justify-center gap-4 mb-8">
                    <button
                        type="button"
                        disabled={permissionState !== 'granted'}
                        onClick={toggleVideo} 
                        className={`p-4 rounded-full transition-colors shadow-lg ${permissionState !== 'granted' ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : isVideoEnabled ? 'bg-white hover:bg-gray-100 cursor-pointer' : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'}`}>
                            <Video />
                    </button>
                    <button
                        type="button"
                        disabled={permissionState !== 'granted'}
                        onClick={toggleAudio}
                        className={`p-4 rounded-full transition-colors shadow-lg ${permissionState !== 'granted' ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : isAudioEnabled ? 'bg-white hover:bg-gray-100 cursor-pointer' : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'}`}>
                            {isAudioEnabled ? <Mic /> : <MicOff />}
                    </button>
                    <button
                        type="button"
                        disabled={permissionState !== 'granted'}
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-4 rounded-full transition-colors shadow-lg ${permissionState !== 'granted' ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : showSettings ? 'bg-indigo-500 text-white cursor-pointer' : 'bg-white hover:bg-gray-100 cursor-pointer'}`}>
                            <Settings />
                    </button>
                </div>
            </div>

            {showSettings && (
                <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm space-y-4 w-full lg:w-1/2 h-auto">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{MESSAGES.labels.camera}</label>
                        <div className="relative">
                            <select
                                value={selectedVideoDevice}
                                onChange={(e) => setSelectedVideoDevice(e.target.value)}
                                disabled={permissionState !== 'granted'}
                                className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-white appearance-none pr-8"
                            >
                                {devices.filter(d => d.kind === 'videoinput').map(d => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `${MESSAGES.labels.fallbackCamera} ${d.deviceId.slice(0, 8)}`}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{MESSAGES.labels.microphone}</label>
                        <div className="relative">
                            <select
                                value={selectedAudioDevice}
                                onChange={(e) => setSelectedAudioDevice(e.target.value)}
                                disabled={permissionState !== 'granted'}
                                className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-white appearance-none pr-8"
                            >
                                {devices.filter(d => d.kind === 'audioinput').map(d => (
                                    <option key={d.deviceId} value={d.deviceId}>{d.label || `${MESSAGES.labels.fallbackMicrophone} ${d.deviceId.slice(0, 8)}`}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CallSettings;